import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "../components/ui/alert-dialog";
import { Upload, FilePdf, FileText, Image as ImageIcon, ArrowsOutSimple, Trash, DownloadSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const fileIcon = (type = "") => {
  const t = type.toLowerCase();
  if (t.includes("pdf")) return <FilePdf size={18} className="text-red-600" />;
  if (t.includes("image")) return <ImageIcon size={18} className="text-blue-600" />;
  if (t.includes("word") || t.includes("doc")) return <FileText size={18} className="text-blue-700" />;
  return <FileText size={18} className="text-slate-600" />;
};

/**
 * CourseSlides — admin/instructor upload, everyone views inline (copy/print/save best-effort blocked).
 * Only super_admin sees a Download button. Uploader (or super_admin/admin) sees a Delete button.
 */
export default function CourseSlides({ courseId, currentUser, canUpload }) {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [activeSlide, setActiveSlide] = useState(null);
  const [shareToken, setShareToken] = useState("");
  const [fullScreen, setFullScreen] = useState(false);
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);
  const isSuperAdmin = currentUser?.role === "super_admin";

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const tokenLs = typeof window !== "undefined" ? localStorage.getItem("sbl_token") || "" : "";

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/courses/${courseId}/slides`);
      setSlides(data || []);
    } catch {
      // noop
    }
    setLoading(false);
  };

  useEffect(() => {
    if (courseId) fetchSlides();
  }, [courseId]);

  // Fetch a fresh share-token whenever a slide is opened (Office/Google viewers need a public URL)
  useEffect(() => {
    setShareToken("");
    if (!activeSlide) return;
    const ct = (activeSlide.content_type || "").toLowerCase();
    const filename = (activeSlide.filename || "").toLowerCase();
    const isOffice = ct.includes("powerpoint") || ct.includes("presentation") ||
      ct.includes("officedocument") || ct.includes("msword") || ct.includes("ms-word") ||
      /\.(pptx?|docx?|xlsx?)$/i.test(filename);
    if (!isOffice) return;
    api.get(`/slides/${activeSlide.id}/share-token`)
      .then((r) => setShareToken(r.data?.token || ""))
      .catch(() => toast.error("Could not load slide preview"));
  }, [activeSlide]);

  const handleUpload = async (e) => {
    e?.preventDefault?.();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Choose a file first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      await api.post(`/courses/${courseId}/slides?title=${encodeURIComponent(title || file.name)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Slide uploaded");
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      setUploadDialog(false);
      fetchSlides();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const handleDelete = async (slide) => {
    try {
      await api.delete(`/slides/${slide.id}`);
      toast.success("Slide deleted");
      fetchSlides();
      if (activeSlide?.id === slide.id) setActiveSlide(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const viewerUrl = (slide) => {
    if (!slide) return "";
    // Same-origin iframe — cookie-based auth applies automatically.
    return `${apiBase}/slides/${slide.id}/view`;
  };

  const handleDownload = async (slide) => {
    try {
      const resp = await api.get(`/slides/${slide.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = slide.filename || "slide";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  // Best-effort copy/print protection on the slide-viewer pane
  const protectiveHandlers = !isSuperAdmin
    ? {
        onContextMenu: (e) => e.preventDefault(),
        onCopy: (e) => e.preventDefault(),
        onCut: (e) => e.preventDefault(),
        onKeyDown: (e) => {
          if ((e.ctrlKey || e.metaKey) && ["c", "p", "s", "u"].includes(e.key.toLowerCase())) {
            e.preventDefault();
          }
        },
      }
    : {};

  return (
    <div className="space-y-3" data-testid="course-slides">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Slides & Documents
          </h3>
          <p className="text-xs text-slate-500">PDFs and slide decks shared by your instructor.</p>
        </div>
        {canUpload && (
          <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-1" data-testid="upload-slide-btn">
                <Upload size={14} /> Upload Slide
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Upload a slide / document</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-3 mt-2">
                <div>
                  <Label className="text-sm">Title</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Module 1 - Vision Setting"
                    className="mt-1 rounded-none"
                    data-testid="slide-title-input"
                  />
                </div>
                <div>
                  <Label className="text-sm">File (PDF, PPT, PPTX, DOC, DOCX, image)</Label>
                  <Input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.ppt,.pptx,.doc,.docx,.png,.jpg,.jpeg,.webp,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                    className="mt-1 rounded-none"
                    data-testid="slide-file-input"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">PowerPoint and Word render via Microsoft Office Online viewer (no download possible for students).</p>
                </div>
                <Button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-1"
                  data-testid="confirm-upload-slide-btn"
                >
                  <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Slides list */}
      {loading ? (
        <div className="h-24 bg-slate-100 animate-pulse" />
      ) : slides.length === 0 ? (
        <Card className="border border-dashed border-slate-300 rounded-none p-8 text-center">
          <FileText size={28} className="text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No slides uploaded yet.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {slides.map((s, idx) => (
            <Card
              key={s.id}
              className={`border border-slate-200 rounded-none p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                activeSlide?.id === s.id ? "border-l-2 border-l-blue-800 bg-blue-50/30" : ""
              }`}
              onClick={() => setActiveSlide(s)}
              data-testid={`slide-card-${idx}`}
            >
              <div className="flex items-start gap-2">
                <div className="mt-0.5">{fileIcon(s.content_type)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{s.title}</p>
                  <p className="text-[10px] text-slate-400 truncate">
                    {s.uploader_name} · {new Date(s.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {s.can_download && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownload(s); }}
                      className="p-1 text-emerald-700 hover:text-emerald-900"
                      title="Download (super admin only)"
                      data-testid={`slide-download-${idx}`}
                    >
                      <DownloadSimple size={14} />
                    </button>
                  )}
                  {s.can_delete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Delete slide"
                          data-testid={`slide-delete-${idx}`}
                        >
                          <Trash size={14} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-none">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this slide?</AlertDialogTitle>
                          <AlertDialogDescription>"{s.title}" will be permanently removed for all students. This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(s)} className="bg-red-700 hover:bg-red-800 rounded-none" data-testid={`confirm-delete-slide-${idx}`}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Inline viewer */}
      {activeSlide && (
        <Card
          className={`border border-slate-200 rounded-none mt-3 ${fullScreen ? "fixed inset-0 z-[60] m-0 bg-white overflow-hidden" : ""}`}
          {...protectiveHandlers}
          style={!isSuperAdmin ? { userSelect: "none", WebkitUserSelect: "none" } : {}}
          data-testid="slide-viewer"
        >
          <div className="flex items-center justify-between p-2 border-b border-slate-200 bg-slate-50">
            <p className="text-sm font-medium text-slate-900 truncate flex-1 px-2">{activeSlide.title}</p>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => window.open(viewerUrl(activeSlide), "_blank", "noopener,noreferrer")}
                className="rounded-none gap-1 text-xs"
                data-testid="slide-open-tab-btn"
                title="Open in new tab (best for printing-disabled previews)"
              >
                Open in new tab
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFullScreen((v) => !v)}
                className="rounded-none gap-1 text-xs"
                data-testid="slide-fullscreen-btn"
              >
                <ArrowsOutSimple size={14} /> {fullScreen ? "Exit Full View" : "Full View"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setActiveSlide(null); setFullScreen(false); }}
                className="rounded-none"
                data-testid="slide-close-btn"
              >
                <X size={14} />
              </Button>
            </div>
          </div>
          <div className={`bg-slate-100 ${fullScreen ? "h-[calc(100vh-3rem)]" : "h-[600px]"} relative`}>
            {/* Render based on file type. Browser's built-in PDF viewer works via <object>/<iframe>; images render directly. */}
            {(() => {
              const ct = (activeSlide.content_type || "").toLowerCase();
              const url = viewerUrl(activeSlide);
              if (ct.startsWith("image/")) {
                return (
                  <img
                    src={url}
                    alt={activeSlide.title}
                    className="w-full h-full object-contain bg-slate-900"
                    crossOrigin="anonymous"
                    draggable={isSuperAdmin}
                    onContextMenu={(e) => !isSuperAdmin && e.preventDefault()}
                    data-testid="slide-img"
                  />
                );
              }
              if (ct.includes("pdf")) {
                // Native browser PDF embed (uses the user's local PDF viewer).
                return (
                  <div className="w-full h-full relative">
                    <embed
                      src={url + "#toolbar=0&navpanes=0"}
                      type="application/pdf"
                      className="w-full h-full"
                      data-testid="slide-pdf"
                    />
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-2 right-2 bg-white border border-slate-300 px-2 py-1 text-[10px] text-slate-700 hover:text-blue-700 hover:border-blue-500 rounded-none shadow-sm"
                      data-testid="slide-pdf-tab-fallback"
                    >
                      Not loading? Open in new tab →
                    </a>
                  </div>
                );
              }
              // PPT/PPTX/DOC/DOCX → render via Microsoft Office Online viewer (free, public).
              const isOffice = ct.includes("powerpoint") || ct.includes("presentation") || ct.includes("officedocument") ||
                ct.includes("msword") || ct.includes("ms-word") ||
                /\.(pptx?|docx?|xlsx?)$/i.test(activeSlide.filename || "");
              if (isOffice) {
                if (!shareToken) {
                  return (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-sm text-slate-500">Loading viewer…</p>
                    </div>
                  );
                }
                const fullUrl = window.location.origin + url + `?token=${encodeURIComponent(shareToken)}`;
                const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fullUrl)}`;
                return (
                  <div className="w-full h-full relative bg-white">
                    <iframe
                      src={officeUrl}
                      title={activeSlide.title}
                      className="w-full h-full border-0"
                      allow="autoplay; fullscreen"
                      data-testid="slide-office"
                    />
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-2 right-2 bg-white border border-slate-300 px-2 py-1 text-[10px] text-slate-700 hover:text-blue-700 hover:border-blue-500 rounded-none shadow-sm"
                    >
                      Slow to load? Open in new tab →
                    </a>
                  </div>
                );
              }
              // PPT / DOC / fallback: use Office Online / Google docs viewer (frontend-only renders requested URL).
              // Since our slide URL requires auth cookies, fall back to a friendly download-prompt for these formats.
              return (
                <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                  <FileText size={48} className="text-slate-400 mb-3" />
                  <p className="text-sm font-medium text-slate-800 mb-1">{activeSlide.title}</p>
                  <p className="text-xs text-slate-500 mb-4">
                    Live preview is only available for PDF and image files. Please ask your instructor to upload as PDF for inline viewing.
                  </p>
                  <p className="text-[10px] text-slate-400">File type: {activeSlide.content_type}</p>
                </div>
              );
            })()}
            {!isSuperAdmin && (
              <>
                {/* Watermark overlay — discourages screenshots & marks the user */}
                <div
                  className="absolute inset-0 flex flex-wrap content-around justify-around pointer-events-none select-none opacity-[0.06]"
                  aria-hidden="true"
                >
                  {Array.from({ length: 24 }).map((_, i) => (
                    <span key={i} className="text-xs font-bold text-slate-900 -rotate-12">
                      SBL · {currentUser?.name || ""}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
          {!isSuperAdmin && (
            <div className="p-2 border-t border-slate-200 bg-amber-50 text-[10px] text-amber-800 flex items-center justify-between">
              <span>This slide is view-only. Download / print / copy are disabled.</span>
              <span className="font-mono">{currentUser?.email || ""}</span>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
